import { useMemo } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  FormControlLabel,
  LinearProgress,
  Switch,
} from '@mui/material';
import { Route, Routes, useNavigate } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { Lots_Set_Input } from '@graphql/__generated__/graphql';
import { getDefaultBuyersMatchingFilters } from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { AutoMatchingCriteriaDrawer } from '@modules/_shared/components/listing-marketing/AutoMatchingCriteriaDrawer';
import { AutoMatchingFilters } from '@modules/_shared/components/listing-marketing/AutoMatchingFilters';
import { BuyersAutoMatchingEmailForm } from '@modules/_shared/components/listing-marketing/BuyersAutoMatchingEmailForm';
import { MarketingHomogeneousTitle } from '@modules/listings/ListingMarketing';
import { GET_LISTING_SURROUNDING_PLACES } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

interface ListingBuyersAutoMatchingProps {
  lotId: string;
  readOnly?: boolean;
}

const GET_LISTING_BUYERS_AUTO_MATCHING_QUERY = gql(/* GraphQL */ `
  query GetListingBuyersAutoMatching($listingId: uuid!) {
    lots_by_pk(id: $listingId) {
      id
      auto_matching_filters
      offer_type
      enable_auto_matching
      ...ListingBuyersAutoMatchingEmail
      ...AutoMatchingFilters
      ...DefaultBuyersMatchingFilters
    }
  }
`);

const UPDATE_LISTING_AUTO_MATCHING = gql(/* GraphQL */ `
  mutation updateListingBuyersAutoMatching(
    $id: uuid!
    $updates: lots_set_input!
  ) {
    update_lots_by_pk(pk_columns: { id: $id }, _set: $updates) {
      id
      enable_auto_matching
      auto_matching_response_enabled
    }
  }
`);

export const ListingBuyersAutoMatching: React.FC<
  ListingBuyersAutoMatchingProps
> = ({ lotId, readOnly = false }) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(
    GET_LISTING_BUYERS_AUTO_MATCHING_QUERY,
    {
      variables: { listingId: lotId },
    },
  );

  const { data: surroundingPlacesData } = useQuery(
    GET_LISTING_SURROUNDING_PLACES,
    {
      variables: { id: lotId },
    },
  );

  const [updateListing, { loading: updatingListing }] = useMutation(
    UPDATE_LISTING_AUTO_MATCHING,
  );

  const listing = data?.lots_by_pk;
  const extendedPlaces =
    surroundingPlacesData?.get_surrounding_listing_places?.map(
      place => place.place_id,
    );

  const initialFilters = useMemo(() => {
    if (listing == null) {
      return null;
    }

    return (
      listing.auto_matching_filters ??
      getDefaultBuyersMatchingFilters(listing, extendedPlaces)
    );
  }, [listing, extendedPlaces]);

  return loading ? (
    <LinearProgress
      sx={{
        position: 'absolute',
        top: 0,
        width: '100%',
        zIndex: 2000,
      }}
    />
  ) : (
    <>
      {error != null && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}
      {listing == null ? (
        <Alert severity="error" sx={{ m: 2 }}>
          {t('No listing data found')}
        </Alert>
      ) : (
        <Box>
          <MarketingHomogeneousTitle title={t('Auto-matching settings')} />

          <FormControlLabel
            sx={{
              pointerEvents: updatingListing || readOnly ? 'none' : 'initial',
            }}
            control={
              <Switch
                disabled={readOnly}
                onChange={() => {
                  const updates: Lots_Set_Input = {
                    enable_auto_matching: !listing.enable_auto_matching,
                  };

                  if (
                    !updates.enable_auto_matching &&
                    listing.auto_matching_response_enabled
                  ) {
                    // If auto-matching is disabled, we need to disable the response email as well.
                    updates.auto_matching_response_enabled = false;
                  }

                  updateListing({
                    variables: {
                      id: listing.id,
                      updates,
                    },
                  });
                }}
                checked={listing.enable_auto_matching}
              />
            }
            label={t('Enable auto-matching')}
          />

          <Box sx={{ mb: 2, mt: 1 }}>
            <AutoMatchingFilters
              listing={listing}
              extendedPlaces={extendedPlaces}
              readOnly={readOnly}
            />
          </Box>

          <MarketingHomogeneousTitle title={t('Email settings')} />

          <BuyersAutoMatchingEmailForm
            listing={listing}
            disabled={readOnly || !listing.enable_auto_matching}
          />

          <Routes>
            <Route
              path="filters"
              element={
                <AutoMatchingCriteriaDrawer
                  listingId={listing.id}
                  initialCriteria={initialFilters}
                  offerType={listing.offer_type}
                  onClose={() => {
                    navigate('./');
                  }}
                />
              }
            />
          </Routes>
        </Box>
      )}
    </>
  );
};
